<script setup>
import { computed, reactive, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { Spinner } from "@/shared/ui/spinner";
import { Tag, pickTagVariant } from "@/shared/ui/tag";
import { useServiceQueueStore } from "@/store/serviceQueue";
import {
  summarizeArrayChoices,
  resolveArrayItemLabel,
} from "@/shared/serviceResponse";

const serviceQueue = useServiceQueueStore();
const { t, locale } = useI18n();

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
  activeService: {
    type: String,
    default: null,
  },
  participant: {
    type: Object,
    default: null,
  },
  selectedParticipant: {
    type: Object,
    default: null,
  },
  isAccessLoading: {
    type: Boolean,
    default: false,
  },
  requestedServices: {
    type: Map,
    default: () => new Map(),
  },
  badgeSize: {
    type: String,
    default: "sm",
  },
  badgeVariant: {
    type: String,
    default: null,
  },
  canEdit: {
    type: Boolean,
    default: true,
  },
});

const resolvedBadgeVariant = computed(
  () => props.badgeVariant || pickTagVariant(props.group?.title || "")
);

const emit = defineEmits([
  "handleClick",
  "change:service",
  "edit",
  "insert:saved-fields",
]);

const expanded = reactive({});

onMounted(() => {
  if (props.group.expanded || props.group.items.length > 0) {
    expanded[props.group.key] = true;
  }
});

function toggleExpand(code) {
  expanded[code] = !expanded[code];
}

function handleClick(val) {
  emit("handleClick", val);
}

function handleEdit() {
  emit("edit", props.participant);
}

function handleRerunQueue() {
  if (!props.participant?.uuid) return;
  if (typeof serviceQueue.rerunQueue === "function") {
    serviceQueue.rerunQueue(props.participant.uuid);
  }
}

const hasQueue = computed(() => {
  if (!props.participant?.uuid) return false;
  if (typeof serviceQueue.getQueueForParticipant !== "function") return false;
  const queue = serviceQueue.getQueueForParticipant(props.participant.uuid);
  return !!queue && queue.total > 0;
});

function changeService(item) {
  emit("change:service", item);
}

function isActive(item) {
  const isActiveService = item.code === props.activeService;
  if (props.selectedParticipant) {
    return (
      props.participant?.uuid === props.selectedParticipant?.uuid &&
      isActiveService
    );
  }
  return isActiveService;
}

function getQueueStatus(item) {
  if (!props.participant?.uuid) return null;
  return serviceQueue.getStepStatus(props.participant.uuid, item.code);
}

function getRequestStatus(item) {
  if (!props.participant?.uuid) return null;
  const queueStatus = getQueueStatus(item);
  if (queueStatus === "success" || queueStatus === "error") return queueStatus;
  // Очередь существует, но шаг ещё не дал терминальный результат
  // (idle/running/skipped) — не откатываемся на устаревший requestedServices
  // (он не чистится при пере-запуске и хранит статус прошлого прогона).
  if (queueStatus) return null;
  return (
    props.requestedServices.get(props.participant.uuid + ":" + item.code) ||
    null
  );
}

function isStepRunning(item) {
  return getQueueStatus(item) === "running";
}

function getStepData(item) {
  if (!props.participant?.uuid) return null;
  return serviceQueue.getStepData(props.participant.uuid, item.code);
}

function getQueueStep(item) {
  if (!props.participant?.uuid) return null;
  if (typeof serviceQueue.getQueueForParticipant !== "function") return null;
  const queue = serviceQueue.getQueueForParticipant(props.participant.uuid);
  if (!queue) return null;
  return queue.steps.find((s) => s.code === item.code) || null;
}

function fieldsCount(item) {
  const data = getStepData(item);
  if (!data) return 0;
  return Object.keys(data).length;
}

function getSavedFields(item) {
  return props.participant?.service_fields?.[item.code] || null;
}

function hasSavedFields(item) {
  const saved = getSavedFields(item);
  return !!saved && Object.keys(saved).length > 0;
}

function savedFieldsCount(item) {
  const saved = getSavedFields(item);
  return saved ? Object.keys(saved).length : 0;
}

const arrayChoicesByCode = computed(() => {
  const map = {};
  const items = props.group?.items || [];
  for (const item of items) {
    const data =
      isStepRunning(item) || getRequestStatus(item) === "error"
        ? null
        : getStepData(item) || getSavedFields(item);
    map[item.code] = data
      ? summarizeArrayChoices(data, { locale: locale.value || "ru" })
      : { multi: [], single: [] };
  }
  return map;
});

function choiceLabel(entry) {
  return resolveArrayItemLabel(entry?.key || "", { t });
}

const savedFieldsTotal = computed(() => {
  const sf = props.participant?.service_fields;
  if (!sf || typeof sf !== "object") return 0;
  return Object.values(sf).reduce((acc, payload) => {
    if (payload && typeof payload === "object") {
      return acc + Object.keys(payload).length;
    }
    return acc;
  }, 0);
});

function onInsertSaved() {
  if (!props.participant?.uuid) return;
  emit("insert:saved-fields", props.participant);
}

const progress = computed(() => {
  if (!props.participant?.uuid) {
    return { total: 0, done: 0, running: false, finished: false };
  }
  const queue = serviceQueue.getQueueForParticipant(props.participant.uuid);
  if (!queue) return { total: 0, done: 0, running: false, finished: false };
  return {
    total: queue.total,
    done: queue.done,
    running: queue.running,
    finished: queue.finished,
  };
});

const percent = computed(() => {
  if (progress.value.total === 0) return 0;
  return Math.round((progress.value.done / progress.value.total) * 100);
});

const progressColor = computed(() => {
  if (progress.value.finished) return "#22c55e"; // green-500
  if (progress.value.running) return "#2563eb"; // blue-600
  return "#94a3b8"; // slate-400
});

const barTextClass = computed(() => {
  if (progress.value.finished) return "text-green-500";
  if (progress.value.running) return "text-blue-600";
  return "text-slate-400";
});
</script>

<template>
  <div
    class="participant-card border border-border flex flex-col"
    style="border-radius: var(--radius)"
  >
    <div
      class="card-head flex justify-between items-center cursor-pointer px-4 py-3"
      @click.stop="toggleExpand(group.key)"
    >
      <Tag
        :variant="resolvedBadgeVariant"
        :size="badgeSize"
        :closable="!!group?.action?.icon"
        :close-icon="group?.action?.icon || 'huge-close'"
        @close="handleClick(group.action)"
        @click.stop
      >
        {{ group.title }}
      </Tag>

      <div class="flex items-center gap-1">
        <button
          v-if="canEdit"
          type="button"
          title="Редактировать"
          class="header-btn"
          @click.stop="handleEdit"
        >
          <SvgIcon name="huge-edit" />
        </button>

        <button
          v-if="hasQueue && canEdit"
          type="button"
          title="Повторить"
          class="header-btn"
          @click.stop="handleRerunQueue"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
        </button>

        <button type="button" class="header-btn">
          <SvgIcon :name="expanded[group.key] ? 'huge-up' : 'huge-down'" />
        </button>
      </div>
    </div>

    <div class="card-divider" />

    <div class="card-body px-4 pt-4">
      <slot name="test" />
    </div>

    <div
      v-show="expanded[group.key]"
      class="card-body p-4 pt-0 flex flex-col gap-3"
    >
      <div v-if="isAccessLoading" class="flex justify-center items-center py-8">
        <Spinner size="sm" />
      </div>
      <template v-else>
        <div v-if="hasQueue && progress.total > 0" class="queue-progress">
          <div class="w-bar-item">
            <div class="w-bar-head">
              <div class="w-bar-label">
                Выполнено {{ progress.done }} из {{ progress.total }}
              </div>
              <div class="w-bar-val" :class="barTextClass">{{ percent }}%</div>
            </div>
            <div class="w-bar-track">
              <div
                class="w-bar-fill"
                :style="{
                  width: percent + '%',
                  backgroundColor: progressColor,
                }"
              ></div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <div
            v-for="item in group.items"
            :key="item.code"
            class="res"
            :class="{
              active: isActive(item),
              ok: getRequestStatus(item) === 'success',
              err: getRequestStatus(item) === 'error',
              run: isStepRunning(item),
              saved:
                !getRequestStatus(item) &&
                !isStepRunning(item) &&
                hasSavedFields(item),
            }"
            @click="changeService(item)"
          >
            <div class="res-row">
              <span class="res-stat">
                <Spinner v-if="isStepRunning(item)" size="sm" />
                <SvgIcon
                  v-else-if="getRequestStatus(item) === 'success'"
                  name="sli-check-circle"
                  class="w-5 h-5 text-green-500"
                  style="fill: none"
                />
                <SvgIcon
                  v-else-if="getRequestStatus(item) === 'error'"
                  name="sli-close2"
                  class="w-5 h-5 text-red-500"
                  style="fill: none"
                />
                <svg
                  v-else-if="hasSavedFields(item)"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="w-4 h-4 text-blue-500"
                >
                  <ellipse cx="12" cy="5" rx="9" ry="3" />
                  <path d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                  <path d="M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6" />
                </svg>
                <span v-else class="idle-dot" />
              </span>

              <div class="res-main">
                <div class="res-name">{{ item.name }}</div>
                <div
                  v-if="getRequestStatus(item) === 'error'"
                  class="res-sub err"
                >
                  {{
                    getQueueStep(item)?.error || "Сервис вернул пустой ответ"
                  }}
                </div>
                <div
                  v-else-if="getRequestStatus(item) === 'success'"
                  class="res-sub"
                >
                  {{ fieldsCount(item) }} полей получено
                </div>
                <div v-else-if="isStepRunning(item)" class="res-sub">
                  Запрос...
                </div>
                <div v-else-if="hasSavedFields(item)" class="res-sub saved">
                  {{ savedFieldsCount(item) }} значений сохранено
                </div>

                <div
                  v-if="arrayChoicesByCode[item.code]?.multi.length"
                  @click.stop="changeService(item)"
                >
                  <div
                    v-for="entry in arrayChoicesByCode[item.code].multi"
                    :key="entry.path"
                    class="res-sub warning"
                  >
                    Выбрать: {{ choiceLabel(entry) }} · {{ entry.count }}
                  </div>
                </div>

                <div
                  v-if="arrayChoicesByCode[item.code]?.single.length"
                  class="res-sub warning"
                >
                  <div
                    v-for="entry in arrayChoicesByCode[item.code].single"
                    :key="entry.path"
                  >
                    {{ choiceLabel(entry) }} · выбран автоматически
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="savedFieldsTotal > 0" class="saved-footer">
          <button
            type="button"
            class="saved-insert-btn"
            @click.stop="onInsertSaved"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="w-4 h-4"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Добавить в документ
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.participant-card {
  overflow: hidden;
}

.header-btn {
  @apply inline-flex items-center justify-center rounded-md text-muted-foreground flex-shrink-0;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: hsl(var(--muted));
    color: hsl(var(--foreground));
  }
}

.card-head {
  background: hsl(var(--muted) / 0.3);
}

.card-body {
  background: hsl(var(--card));
}

.card-divider {
  border-top: 1px solid hsl(var(--border));
}

.queue-progress {
  @apply px-1 pb-2;
}

.w-bar-item {
  @apply flex flex-col gap-1.5;
}

.w-bar-head {
  @apply flex items-center justify-between;
}

.w-bar-label {
  @apply text-muted-foreground font-medium;
  font-size: 13px;
}

.w-bar-val {
  @apply font-bold;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.w-bar-track {
  @apply w-full rounded-full overflow-hidden bg-gray-100;
  height: 6px;
}

.w-bar-fill {
  @apply h-full rounded-full;
  transition: width 0.6s ease;
}

.res {
  @apply rounded-lg overflow-hidden cursor-pointer;
  background: hsl(var(--background));
  border: 1px solid hsl(var(--border));
  transition: all 0.15s ease;

  &:hover {
    background: hsl(var(--muted));
  }

  &.active {
    background-color: hsl(var(--muted));
    border-color: hsl(var(--foreground));
  }

  &.err {
    border-color: #fecaca;
  }

  &.saved {
    //border-color: #bfdbfe;
    //background: #eff6ff;
  }
}

.saved-footer {
  @apply pt-2;
  border-top: 1px dashed hsl(var(--border));
}

.saved-insert-btn {
  @apply inline-flex items-center justify-center gap-1.5 w-full rounded-md px-3 py-2 text-[13px] font-medium;
  color: hsl(var(--primary));
  background: transparent;
  border: 1px solid hsl(var(--border));
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: hsl(var(--muted));
  }
}

.res-row {
  @apply flex items-center gap-2.5 px-3 py-2;
  cursor: pointer;
}

.res-stat {
  @apply flex items-center justify-center flex-shrink-0;
  width: 24px;
  height: 24px;
}

.idle-dot {
  @apply rounded-full bg-muted-foreground/30;
  width: 8px;
  height: 8px;
}

.res-main {
  @apply flex flex-col gap-0.5 flex-1 min-w-0;
}

.res-name {
  @apply text-[13px] text-foreground;

  .dark {
    @apply text-muted-foreground;
  }
}

.res-sub {
  @apply text-[11px] text-muted-foreground truncate;

  &.err {
    //color: #dc2626;
  }

  &.warning {
    @apply text-yellow-600;
  }
}

.res-choices {
  @apply flex flex-wrap gap-1 mt-1;
}
</style>
